"use client";

import React from "react";
import { Sun, ShieldCheck, CalendarSync, Building2, MapPin, PhoneCall, ArrowRight, CircleCheck as CheckCircle2, Clock, Handshake } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const installerNeeds = [{
  title: "Dependable roof access",
  description: "Stable working platforms and safe loading points built to exact eaves heights for smooth panel handling.",
  iconName: "ShieldCheck"
}, {
  title: "Edge protection systems",
  description: "Compliant handrails, toe boards, and debris netting fitted exactly where your PV array layout demands.",
  iconName: "Sun"
}, {
  title: "Programme-aligned builds",
  description: "Scaffolding ready on schedule so your electrical and mounting crews never lose productive days on site.",
  iconName: "CalendarSync"
}, {
  title: "Repeat contractor pricing",
  description: "Predictable commercial rates and streamlined call-offs for residential rollouts and commercial PV projects.",
  iconName: "Handshake"
}];
const relevantServices = [{
  title: "Solar access scaffolding",
  description: "Dedicated roof-level platforms configured specifically for solar panel lifting and array installation.",
  to: "/SolarScaffoldingPage",
  badge: "Specialist access"
}, {
  title: "Edge protection",
  description: "Temporary edge guardrails and safety barriers for flat and pitched roofs during PV fitting.",
  to: "/EdgeProtectionPage",
  badge: "Safety compliant"
}, {
  title: "Residential scaffolding",
  description: "Clean, courteous domestic setups for retrofit domestic solar schemes and homeowner properties.",
  to: "/ResidentialScaffoldingPage",
  badge: "Domestic installs"
}];
const coverageAreas = [{
  name: "Berkshire"
}, {
  name: "Surrey"
}, {
  name: "Hampshire"
}, {
  name: "Buckinghamshire"
}, {
  name: "Greater London"
}, {
  name: "South East"
}];
export default function SolarHero() {
  return <section id="solarinstallerhero" data-nav="light" className="relative w-full bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Header & Answer-First Intro */}
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center gap-2">
            <Badge variant="outline" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold border-primary/30 text-primary bg-primary/5">
              <Sun className="size-3.5 mr-1" />
              Specialist Solar Access Partner
            </Badge>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground font-default">
            Scaffolding for solar installers
          </h1>

          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
            Safe roof access, edge protection, and reliable scaffold handovers scheduled around your solar installation programme.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
              <Link to="/ContactRequestQuotePage">
                Discuss solar project access
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted">
              <Link to="/AreasWeCoverPage">
                <MapPin className="size-4 mr-1 text-primary" />
                View South East coverage
              </Link>
            </Button>
          </div>

          {/* Quick Credential Strip */}
          <div className="pt-6 flex flex-wrap items-center justify-center gap-y-2 gap-x-8 text-sm text-muted-foreground">
            <div className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="size-4 text-primary shrink-0" />
              <span>NASC &amp; CHAS accredited</span>
            </div>
            <div className="inline-flex items-center gap-1.5">
              <Clock className="size-4 text-primary shrink-0" />
              <span>Prompt turnarounds &amp; scheduled striking</span>
            </div>
            <div className="inline-flex items-center gap-1.5">
              <Building2 className="size-4 text-primary shrink-0" />
              <span>Residential &amp; commercial solar arrays</span>
            </div>
          </div>
        </div>

        {/* Core Value Proposition Grid */}
        <div className="mt-16 pt-12 border-t border-border">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-default">
              What solar contractors need from their access partner
            </h2>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">
              BMF Scaffolding works as a dependable trade partner so your mounting and electrical teams can install without delays.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {installerNeeds.map((item, i) => <div data-index={i} key={item.title} className="h-full">
                <Card className="h-full border border-border bg-card shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
                  <CardHeader className="space-y-3 pb-3">
                    <div className="size-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                      {i === 0 && <ShieldCheck className="size-6" />}
                      {i === 1 && <Sun className="size-6" />}
                      {i === 2 && <CalendarSync className="size-6" />}
                      {i === 3 && <Handshake className="size-6" />}
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Partnership & Commercial Section */}
        <div className="mt-16 bg-secondary text-secondary-foreground rounded-2xl p-8 sm:p-10 lg:p-12 shadow-md">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <Badge variant="outline" className="border-secondary-foreground/20 text-secondary-foreground bg-secondary-foreground/10">
                Contractor Partnerships
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight font-default">
                A dependable partner for recurring installs, not a one-off supplier
              </h2>
              <p className="text-secondary-foreground/80 leading-relaxed text-sm sm:text-base">
                We understand that solar installation businesses depend on prompt erection dates, clear handover certificates, and tidy strikes once commissioned. BMF provides dedicated project points of contact, repeat contractor terms, and consistent communication from site survey through to final dismantle.
              </p>
              <div className="pt-2 flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-sm text-secondary-foreground/90">
                  <CheckCircle2 className="size-4 text-primary" />
                  <span>Direct phone coordination with project managers</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-secondary-foreground/90">
                  <CheckCircle2 className="size-4 text-primary" />
                  <span>Transparent contractor rate schedules</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 bg-card text-card-foreground rounded-xl p-6 border border-border shadow-sm space-y-4">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <PhoneCall className="size-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-sm">Solar installer enquiries</h3>
                  <p className="text-xs text-muted-foreground">Direct trade assistance &amp; estimates</p>
                </div>
              </div>
              <Separator />
              <p className="text-xs text-muted-foreground leading-relaxed">
                Whether you have an upcoming commercial PV roof tender or an ongoing residential rollout across the Home Counties, send us your access schedule.
              </p>
              <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactRequestQuotePage">Request trade contractor rates</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Related Services Links */}
        <div className="mt-16 space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground font-default">
                Related solar &amp; roofing access services
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Complementary scaffolding packages tailored for roofing and renewable energy installations.
              </p>
            </div>
            <Button asChild variant="link" className="p-0 h-auto font-semibold text-primary self-start sm:self-auto">
              <Link to="/ProjectsCaseStudiesPage">
                View project case studies
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relevantServices.map((service, i) => <div data-index={i} key={service.title} className="h-full">
                <Card className="h-full border border-border bg-card flex flex-col justify-between shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
                  <CardHeader className="space-y-2">
                    <Badge variant="secondary" className="w-fit text-xs font-medium">
                      {service.badge}
                    </Badge>
                    <CardTitle className="text-lg font-semibold text-card-foreground">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <Button asChild variant="outline" size="sm" className="w-full justify-between text-xs font-medium border-border hover:bg-muted">
                      <Link to={service.to}>
                        <span>Explore service</span>
                        <ArrowRight className="size-3.5 ml-1" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Geographic Coverage Strip */}
        <div className="mt-16 rounded-xl border border-border bg-muted/40 p-6 sm:p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-foreground font-semibold text-sm sm:text-base">
                <MapPin className="size-4 text-primary shrink-0" />
                <span>Regional coverage for solar contractors</span>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Providing prompt scaffolding mobilization across the South East and Home Counties.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {coverageAreas.map((area, i) => <div data-index={i} key={area.name}>
                  <Badge variant="outline" className="bg-background text-foreground text-xs py-1 px-2.5 font-normal border-border">
                    {area.name}
                  </Badge>
                </div>)}
              <Button asChild variant="link" size="sm" className="text-xs text-primary font-semibold pl-1">
                <Link to="/AreasWeCoverPage">View full coverage map</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}